import React, { useState, useRef, useEffect } from "react";
import L, { divIcon } from "leaflet"; // Import Leaflet for 'L' references
import axios from "axios";

import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";

import { Box } from "@mui/material";
import { useMaterialUIController } from "context";
import { v4 as uuidv4 } from "uuid";
import ShapeNameDialog from "./AlertsDialogs/ShapeNameDialog";
import { useFolderStructure } from "./hooks/useFolderStructure";
import { enqueueSnackbar } from "notistack";
import {
  useCreatePolygonMutation,
  useCreateRouteMutation,
} from "features/geofenceSlice";
import * as turf from "@turf/turf";
import { useGeometryDataJSON } from "./useGeometryDataJSON";
import MapHeader from "./components/MapHeader";
import CustomMapContainer from "./components/MapContainer";
import UserShiftDetails from "./components/UserShiftDetails";
import { useGetRouteCoordinateListMutation } from "features/geofenceSlice";
import dayjs from "dayjs";
import { useGetUserShiftDetailsMutation } from "features/geofenceSlice";

const MapComponent = ({
  onSavePolygon,
  defaultLocation,
  onFolderAdd,
  currentFolder,
  setCurrentFolder,
  polygonList,
  setPolygonList,
  folders,
  setFolders,
  folderPath,
  handleFolderClick,
  areaList,
  routeList,
}) => {
  const [polygons, setPolygons] = useState([]); // Store drawn polygons
  const featureGroupRef = useRef(null); // Reference for FeatureGroup
  const mapRef = useRef(null); // Map reference for view management
  const currentFolderRef = useRef(currentFolder);

  const [controller, dispatch] = useMaterialUIController();
  const { darkMode } = controller;

  const [drawingColor, setDrawingColor] = useState("#0000FF"); // Default blue color
  const [colorPickerAnchor, setColorPickerAnchor] = useState(null);
  const [activeDrawingTool, setActiveDrawingTool] = useState(true);

  const [openNameDialog, setOpenNameDialog] = useState(false);
  const [tempShapeData, setTempShapeData] = useState(null);
  const [shapeName, setShapeName] = useState("");
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(13);
  //P_2
  const [showUserLocations, setShowUserLocations] = useState(true);

  const { handleNewFolderStructure } = useFolderStructure(setFolders);
  const { changeGeometryDataJSON } = useGeometryDataJSON();
  const [createPolygon] = useCreatePolygonMutation();
  const [createRoute] = useCreateRouteMutation();
  //P_2
  const [bounds, setBounds] = useState(L.latLngBounds([]));
  const [optionsCheckBox, setOptionsCheckBox] = useState({
    showUsers: true,
    showRoutes: false,
    showZones: false,
    showOverlapping: false,
  });

  const [userShiftDetails, setUserShiftDetails] = useState({
    isLoading: true,
    isError: "",
    data: null,
  });
  const [geoLocationJSONData, setGeoLocationJSONData] = useState(null); // Initialize state for GeoJSON data

  const [isFullScreen, setIsFullScreen] = useState(false); // State to track full-screen mode
  const [toggleUserInfo, setToggleUserInfo] = useState(false);
  const [getRouteCoordinateList] = useGetRouteCoordinateListMutation();
  const [getUserShiftDetails] = useGetUserShiftDetailsMutation();

  const [historyData, setHistoryData] = useState();

  const handleGetRouteCoordinateList = async (startDate, endDate) => {
    if (!startDate || !endDate) return;

    if (!startDate || !endDate) {
      console.error("Invalid start or end date");
      return;
    }

    const payload = {
      currentPage: 0,
      pageSize: 0,
      sortDirection: "desc",
      sortBy: "createdBy",
      search: currentFolder?.name || currentFolder?.userData?.username,
      sortDataType: "string",
      advancedFilters: [
        {
          direction: "from",
          dataType: "date",
          fieldName: "epochDate",
          value: startDate,
        },
        {
          direction: "to",
          dataType: "date",
          fieldName: "epochDate",
          value: endDate,
        },
      ],
    };
    try {
      const response = await getRouteCoordinateList(payload);
      setHistoryData(response?.data?.currentPageData);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(async () => {
    if (currentFolder?.type === "user") {
      const today = dayjs();
      const startDate = today.subtract(2, "hours").format("YYYY-MM-DDTHH:mm");
      const endDate = today.endOf("day").format("YYYY-MM-DDTHH:mm");

      handleGetRouteCoordinateList(startDate, endDate);
      fetchUserShiftDetails();
      // const res = await getUserShiftDetailsAndRouteZones(
      //   currentFolder?.userData?.username
      // );
      // if (res?.data?.data) {
      //   setparticularUserZonesAndRoutes({
      //     zones: res?.data?.data?.areaData
      //       ? {
      //           ...res?.data?.data?.areaData,
      //           areaType: "polygon",
      //         }
      //       : null,
      //     routes: res?.data?.data?.userShiftData
      //       ? [
      //           res?.data?.data?.userShiftData?.map((item) => ({
      //             ...item.routeData,
      //             areaType: "polyline",
      //           })),
      //         ]
      //       : null,
      //   });
      // }
    }
  }, [currentFolder]);

  useEffect(() => {
    if (!featureGroupRef.current) return;

    const featureGroup = featureGroupRef.current;
    // featureGroup.clearLayers();

    if (historyData?.length > 0) {
      // Group points by date
      const journeysByDate = historyData.reduce((acc, point) => {
        const date = new Date(point.timestamp).toLocaleDateString();
        if (!acc[date]) acc[date] = [];
        acc[date].push(point);
        return acc;
      }, {});

      // Create paths for each day
      Object.entries(journeysByDate).forEach(([date, points]) => {
        const pathPoints = points.map((point) => [
          point.latitude,
          point.longitude,
        ]);

        if (pathPoints.length > 1) {
          // Create the path
          const path = L.polyline(pathPoints, {
            color: "#d40617",
            weight: 3,
            opacity: 0.8,
            dashArray: "10, 10",
            lineCap: "round",
          }).addTo(featureGroup);

          // Add markers for each point
          points.forEach((point, index) => {
            const isEndPoint = index === 0 || index === points.length - 1;
            const marker = L.circleMarker([point.latitude, point.longitude], {
              radius: isEndPoint ? 12 : 8,
              fillColor:
                index === 0
                  ? "#4CAF50"
                  : index === points.length - 1
                  ? "#f44336"
                  : "#2196F3",
              color: "#fff",
              weight: 2,
              opacity: 1,
              fillOpacity: 0.8,
              zIndexOffset: isEndPoint ? 1000 : 500,
            }).addTo(featureGroup);

            // Enhanced popup content
            const timestamp = new Date(point.epochDate).toLocaleString();
            const popupContent = `
              <div style="min-width: 200px;">
                <strong>Date:</strong> ${timestamp}<br/>
                <strong>Time:</strong> ${new Date(
                  point.epochDate
                ).toLocaleTimeString()}<br/>
                ${
                  point.gpsAccuracy
                    ? `<strong>Accuracy:</strong> ${point.gpsAccuracy} m<br/>`
                    : ""
                }
                ${
                  point.address
                    ? `<strong>Location:</strong> ${point.address}<br/>`
                    : ""
                }
              </div>
            `;

            marker.bindPopup(popupContent);
            marker.on("mouseover", function () {
              this.openPopup();
            });
            marker.on("mouseout", function () {
              this.closePopup();
            });
          });

          // Add arrow decorations to show direction
          L.polylineDecorator(path, {
            patterns: [
              {
                offset: "5%",
                repeat: "10%",
                symbol: L.Symbol.arrowHead({
                  pixelSize: 15,
                  polygon: false,
                  pathOptions: {
                    color: "#d40617",
                    fillOpacity: 1,
                    weight: 2,
                  },
                }),
              },
            ],
          }).addTo(featureGroup);
        }
      });

      // Fit bounds to show all points
      const allPoints = historyData.map((point) => [
        point.latitude,
        point.longitude,
      ]);
      const bounds = L.latLngBounds(allPoints);
      mapRef.current?.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [historyData]);

  const handleHistoryFilterChange = ({ startDate, endDate }) => {
    handleGetRouteCoordinateList(startDate, endDate);
  };

  // Function to toggle full-screen mode
  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
    if (mapRef.current) {
      setTimeout(() => {
        mapRef.current.invalidateSize();
      }, 100); // Delay to ensure the DOM has updated
    }
  };

  // Function to handle escape key press
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && isFullScreen) {
        setIsFullScreen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFullScreen]);

  // Update the ref whenever currentFolder changes
  useEffect(() => {
    currentFolderRef.current = currentFolder;
  }, [currentFolder]);

  const [currentLocation, setCurrentLocation] = useState(defaultLocation);

  const collectShapesIds = (folder) => {
    if (!folder) return [];

    let ids = [];

    // Get shapes from current folder
    if (folder.shapesId) {
      ids = ids.concat(folder.shapesId);
    }

    // Recursively get shapes from children
    if (folder.children) {
      folder.children.forEach((child) => {
        ids = ids.concat(collectShapesIds(child));
      });
    }

    return ids;
  };

  // Update the getShapesIdsForFolder function
  const getShapesIdsForFolder = (folder) => {
    if (!folder) return [];

    let ids = [];

    // For project type, get shapes from both zones and routes
    if (folder.type === "project") {
      const zonesFolder = folder.children?.find(
        (child) => child.type === "zones"
      );
      const routesFolder = folder.children?.find(
        (child) => child.type === "routes"
      );

      if (zonesFolder) {
        ids = ids.concat(collectShapesIds(zonesFolder));
      }
      if (routesFolder) {
        ids = ids.concat(collectShapesIds(routesFolder));
      }
    }
    // For zones or routes type, get shapes from all children
    else if (folder.type === "zones" || folder.type === "routes") {
      folder.children?.forEach((child) => {
        if (child.shapesId) {
          ids = ids.concat(child.shapesId);
        }
      });
    }
    // For individual zone/route, get its own shapesId
    else if (folder.shapesId) {
      ids = ids.concat(folder.shapesId);
    }

    return ids;
  };

  const getShapedIdsFromCurrentFolder = (currentFolder) => {
    const shapeIds = [];
    const findShapesRecursively = (folder) => {
      if (folder?.type === "user") {
        shapeIds.push(folder.name || folder.userData.username);
      }
      if (folder?.shapesId) {
        shapeIds.push(folder?.shapesId[0]);
      }
      folder?.children?.forEach((child) => {
        if (child.type === "user") {
          shapeIds.push(child.name || child.userData.username);
        }
        if (child.shapesId) {
          shapeIds.push(child.shapesId[0]);
        }
        if (child.children && child.children.length > 0) {
          findShapesRecursively(child);
        }
      });
    };
    findShapesRecursively(currentFolder);
    return shapeIds;
  };

  //P_2
  function getMarkerIcon(color) {
    const customIcon = divIcon({
      html: `<svg class="MuiSvgIcon-root MuiSvgIcon-colorSuccess MuiSvgIcon-fontSizeLarge css-quk7e7-MuiSvgIcon-root" focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="FmdGoodIcon"><path fill=${
        color || "#807e6e"
      } d="M12 2c-4.2 0-8 3.22-8 8.2 0 3.32 2.67 7.25 8 11.8 5.33-4.55 8-8.48 8-11.8C20 5.22 16.2 2 12 2m0 10c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2"></path></svg>`,
    });
    customIcon.options.iconSize = [24, 24];
    customIcon.options.iconAnchor = [12, 24];
    return customIcon;
  }

  const convertToTurfFeature = (shape) => {
    const coordinates = shape.coordinates.map((coord) => [
      parseFloat(coord.longitude),
      parseFloat(coord.latitude),
    ]);

    if (shape.areaType === "polygon") {
      return turf.polygon([coordinates]);
    } else if (shape.areaType === "polyline") {
      return turf.lineString(coordinates);
    }
    return null;
  };

  const isSelfIntersecting = (shape) => {
    const feature = convertToTurfFeature(shape);
    if (!feature) return false;

    // Use `turf.kinks()` for self-intersection detection
    const kinks = turf.kinks(feature);
    return kinks.features.length > 0;
  };

  const overlappingShapes = (data) => {
    const result = new Set();

    data.forEach((shape1, index1) => {
      const feature1 = convertToTurfFeature(shape1);
      if (!feature1 || isSelfIntersecting(shape1)) return;

      data.forEach((shape2, index2) => {
        if (index1 === index2) return;

        const feature2 = convertToTurfFeature(shape2);
        if (!feature2 || isSelfIntersecting(shape2)) return;

        if (turf.booleanIntersects(feature1, feature2)) {
          result.add(shape1);
          result.add(shape2);
        }
      });
    });

    return Array.from(result);
  };

  const fetchUserShiftDetails = async () => {
    try {
      const response = await getUserShiftDetails(
        currentFolder?.name || currentFolder?.userData?.name
      );
      setUserShiftDetails({
        isLoading: false,
        isError: false,
        data: response?.data?.data || [],
      });
    } catch (error) {
      setUserShiftDetails({
        isLoading: false,
        isError: error,
        data: null,
      });
    }
  };

  useEffect(() => {
    const updateMap = async () => {
      if (!currentFolder || !featureGroupRef.current) return;

      const featureGroup = featureGroupRef.current;
      featureGroup.clearLayers();

      const shapesIds = getShapedIdsFromCurrentFolder(currentFolder);

      let polygonData = polygonList.reduce((acc, item) => {
        const id = item.shapeId || item.userId;
        if (
          shapesIds.includes(id) &&
          !acc.some(
            (existingItem) =>
              (existingItem.shapeId || existingItem.userId) === id
          )
        ) {
          acc.push(item);
        }
        return acc;
      }, []);

      // Logic for overlapping
      if (optionsCheckBox.showOverlapping) {
        if (currentFolder?.type === "project") {
          const usersData = polygonData.filter(
            (item) => item.areaType === "marker"
          );
          const overlappingData = overlappingShapes(polygonData);
          polygonData = [...usersData, ...overlappingData];
        }

        if (
          currentFolder?.type === "zone" ||
          currentFolder?.type === "polygon" ||
          currentFolder?.type === "geofence" ||
          currentFolder?.type === "zones"
        ) {
          const usersData = polygonData.filter(
            (item) => item.areaType === "marker"
          );
          const currentZone = polygonData.filter(
            (item) => item.areaType === "polygon"
          );
          const totalRoutes = polygonList.filter(
            (item) => item.areaType === "polyline"
          );
          const shapesData = [...currentZone, ...totalRoutes];
          const overlappingData = overlappingShapes(shapesData);
          polygonData = [...usersData, ...overlappingData];
        }

        if (
          currentFolder?.type === "routes" ||
          currentFolder?.type === "polyline" ||
          currentFolder?.type === "route"
        ) {
          const userData = polygonData.filter(
            (item) => item.areaType === "marker"
          );
          const currentRoutes = polygonData.filter(
            (item) => item.areaType === "polyline"
          );
          const totalZones = polygonList.filter(
            (item) => item.areaType === "polygon"
          );

          const shapesData = [...currentRoutes, ...totalZones];
          const overlappingData = overlappingShapes(shapesData);
          polygonData = [...userData, ...overlappingData];
        }
      }
      if (currentFolder?.type === "user" && optionsCheckBox.showZones) {
        const currentZoneOnUser = userShiftDetails?.data
          ?.map((item) => ({
            ...item.zoneData,
            areaType: "polygon",
          }))
          .filter((item) => item !== null);
        if (currentZoneOnUser.length > 0) {
          polygonData = [...polygonData, ...currentZoneOnUser];
        } else {
          enqueueSnackbar("No zone found for this user", {
            variant: "warning",
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
          });
        }
      }
      if (currentFolder?.type === "user" && optionsCheckBox.showRoutes) {
        const currentRouteOnUser = userShiftDetails?.data
          ?.map((item) => ({ ...item.routeData, areaType: "polyline" }))
          .filter((item) => item !== null);
        if (currentRouteOnUser.length > 0) {
          polygonData = [...polygonData, ...currentRouteOnUser];
        } else {
          enqueueSnackbar("No route found for this user", {
            variant: "warning",
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
          });
        }
      }
      const newBounds = L.latLngBounds([]);
      polygonData = polygonData.filter((item) => item && item.coordinates);
      polygonData.forEach((item) => {
        const latLngs = item?.coordinates?.map((coord) => [
          coord.latitude,
          coord.longitude,
        ]);

        if (item.areaType === "polyline" || item.areaType === "route") {
          const polyline = L.polyline(latLngs, {
            color: "red",
            weight: 3,
          }).addTo(featureGroup);

          // Add arrows to the polyline
          L.polylineDecorator(polyline, {
            patterns: [
              {
                offset: "5%",
                repeat: "10%",
                symbol: L.Symbol.arrowHead({
                  pixelSize: 15,
                  polygon: false,
                  pathOptions: { stroke: true, color: "red" },
                }),
              },
            ],
          }).addTo(featureGroup);

          // Add start and end markers
          const startPoint = latLngs[0];
          const endPoint = latLngs[latLngs.length - 1];

          L.circleMarker(startPoint, {
            radius: 8,
            fillColor: "#4CAF50",
            color: "#fff",
            weight: 2,
            opacity: 1,
            fillOpacity: 0.8,
          })
            .addTo(featureGroup)
            .bindPopup("Start Point")
            .on("mouseover", function () {
              this.openPopup();
            })
            .on("mouseout", function () {
              this.closePopup();
            });

          L.circleMarker(endPoint, {
            radius: 8,
            fillColor: "#f44336", // Red for end
            color: "#fff",
            weight: 2,
            opacity: 1,
            fillOpacity: 0.8,
          })
            .addTo(featureGroup)
            .bindPopup("End Point")
            .on("mouseover", function () {
              this.openPopup();
            })
            .on("mouseout", function () {
              this.closePopup();
            });
          if (item.name) {
            polyline.bindTooltip(item.name, {
              permanent: true,
              direction: "center",
              className: "custom-label",
              opacity: 0.8,
              style: {
                background: "none",
                border: "none",
                boxShadow: "none",
                color: item.color || "#0000FF",
                fontWeight: "bold",
              },
            });
          }

          newBounds.extend(polyline.getBounds());
        } else if (item.areaType === "polygon" || item.areaType === "circle") {
          if (item.areaType === "circle" && item.coordinates[0].radius) {
            const circle = L.circle(latLngs[0], {
              color: item.color || "#0000FF",
              fillColor: item.color || "#0000FF",
              fillOpacity: 0.2,
              radius: item.coordinates[0].radius,
            }).addTo(featureGroup);

            if (item.name) {
              circle.bindTooltip(item.name, {
                content: item.name,
                permanent: true,
                direction: "center",
                className: "custom-label",
                opacity: 0.5,
                style: {
                  backgroundColor: "#807e6e",
                  border: "none",
                  boxShadow: "none",
                  color: item.color || "#0000FF",
                  fontWeight: "bold",
                },
              });
            }

            newBounds.extend(circle.getBounds());
          } else {
            const polygon = L.polygon(latLngs, {
              color: item.color || "#0000FF",
              fillColor: item.color || "#0000FF",
              fillOpacity: 0.2,
            }).addTo(featureGroup);

            if (item.name) {
              const tooltip = L.tooltip({
                permanent: true,
                direction: "auto",
                opacity: 0.8,
                offset: [0, -20],
              })
                .setLatLng(latLngs[0])
                .setContent(item.name)
                .addTo(featureGroup);
            }

            newBounds.extend(polygon.getBounds());
          }
        } else if (item.areaType === "marker" && optionsCheckBox.showUsers) {
          const markerLocation = [
            item.coordinates[0].latitude,
            item.coordinates[0].longitude,
          ];
          const customIcon = getMarkerIcon(item.colorCode); // Assuming getMarkerIcon is a function that returns a custom icon

          const marker = L.marker(markerLocation, {
            icon: customIcon,
            draggable: false,
          }).addTo(featureGroup);

          if (item.userId) {
            const tooltipContent = `
              <div style="min-width: 150px;">
                <strong>Name:</strong> ${item.userId}<br/>
                <strong>Coordinates:</strong> ${markerLocation[0]}, ${markerLocation[1]}<br/>
              </div>
            `;
            marker
              .bindPopup(tooltipContent)
              .on("mouseover", function () {
                this.openPopup();
              })
              .on("mouseout", function () {
                this.closePopup();
              });
          }
          newBounds.extend(marker.getLatLng());
        }
      });

      setBounds(newBounds);

      const handleZoomChange = () => {
        const currentZoom = mapRef.current.getZoom();
        const showLabels = currentZoom >= 13; // Set your desired zoom level here

        featureGroup.eachLayer((layer) => {
          if (layer.getTooltip()) {
            if (showLabels) {
              layer.openTooltip();
            } else {
              layer.closeTooltip();
            }
          }
        });
      };

      if (mapRef.current) {
        mapRef.current.on("zoomend", handleZoomChange);
      }

      // Initial call to set the correct state
      handleZoomChange();
    };
    updateMap();
  }, [currentFolder, polygonList, optionsCheckBox, folders, mapRef.current]);

  const handleColorPickerOpen = (event) => {
    setColorPickerAnchor(event.currentTarget);
  };

  const handleColorPickerClose = () => {
    setColorPickerAnchor(null);
  };

  useEffect(() => {
    // Modify L.drawLocal to change toolbar tooltip
    L.drawLocal.draw.toolbar.buttons.polyline = "Create Route";
    L.drawLocal.draw.toolbar.buttons.polygon = "Create Zone";
  }, []);

  const handleColorChange = (color) => {
    setDrawingColor(color.hex);
    if (featureGroupRef.current) {
      const drawControl = featureGroupRef.current._map.drawControl;
      if (drawControl) {
        // Update the drawing color for both polygon and polyline
        if (activeDrawingTool === "polygon") {
          drawControl.setDrawingOptions({
            polygon: { shapeOptions: { color: color.hex } },
          });
        } else if (activeDrawingTool === "polyline") {
          drawControl.setDrawingOptions({
            polyline: { shapeOptions: { color: color.hex } },
          });
        }
      }
    }
  };

  const handleColorDoubleClick = (e, color) => {
    setColorPickerAnchor(null);
    setDrawingColor(color.hex);
  };

  // Add this effect to track the active drawing tool
  useEffect(() => {
    if (featureGroupRef.current) {
      const map = featureGroupRef.current._map;
      map.on("draw:drawstart", (e) => {
        setActiveDrawingTool(e.layerType);
      });
      map.on("draw:drawstop", () => {
        setActiveDrawingTool(null);
      });
    }
  }, []);

  // Handle polygon or polyline creation
  const handleCreate = async (e) => {
    const { layer } = e;

    let shapeId = uuidv4();
    let shapeData = {
      id: shapeId,
      shapeName: "",
      description: "",
      coordinates: [],
      color: drawingColor,
    };

    if (layer instanceof L.Polyline && !(layer instanceof L.Polygon)) {
      // Handle polyline (route)
      const latLngs = layer.getLatLngs();
      shapeData.coordinates = latLngs.map((point) => ({
        lat: point.lat,
        lng: point.lng,
      }));
      shapeData.areaType = "polyline"; // Keep this as "polyline"

      // Set the polyline color to red
      layer.setStyle({ color: "red" }); // Apply red color to the polyline
    } else if (layer instanceof L.Polygon || layer instanceof L.Rectangle) {
      const latLngs = layer.getLatLngs();
      shapeData.coordinates = Array.isArray(latLngs[0])
        ? latLngs[0].map((point) => ({ lat: point.lat, lng: point.lng }))
        : latLngs.map((point) => ({ lat: point.lat, lng: point.lng }));

      shapeData.areaType = "polygon";

      // Add temporary label until name is set
      layer.bindTooltip("Unnamed Zone", {
        permanent: true,
        direction: "center",
        className: "custom-label",
        style: {
          background: "none",
          border: "none",
          boxShadow: "none",
          color: drawingColor,
          fontWeight: "bold",
        },
      });
    } else if (layer instanceof L.Circle) {
      const center = layer.getLatLng();
      shapeData.coordinates = [
        { lat: center.lat, lng: center.lng, radius: layer.getRadius() },
      ];
      shapeData.areaType = "circle";
    }

    const currentFolder = currentFolderRef.current;

    if (
      ((currentFolder.type === "route" && shapeData.areaType === "polyline") ||
        (currentFolder.type === "geofence" &&
          (shapeData.areaType === "polygon" ||
            shapeData.areaType === "circle"))) &&
      currentFolder?.shapesId?.length <= 0
    ) {
      try {
        const payload = {
          name: currentFolder.name,
          shapeId: shapeId,
          coordinates: shapeData.coordinates.map((coord) => ({
            longitude: coord.lng,
            latitude: coord.lat,
          })),
        };

        const response = await (shapeData.areaType === "polyline"
          ? createRoute(payload)
          : createPolygon(payload)
        ).unwrap();

        if (response?.status === 0) {
          handleUpdateFolder(
            currentFolder.id,
            response?.data?.id || "",
            shapeId
          );
          currentFolder.shapesId.push(shapeId);
          currentFolder.db_id = response?.data?.id || "";
          // enqueueSnackbar(
          //   response?.messageDetail
          //     ? response.messageDetail
          //     : `${
          //         shapeData.areaType === "polygon" ? "Geofence" : "Route"
          //       } created successfully`,
          //   {
          //     variant: "success",
          //     anchorOrigin: {
          //       vertical: "top",
          //       horizontal: "right",
          //     },
          //   }
          // );
        }
      } catch (error) {
        console.log("error Failed to create shape", error);
        enqueueSnackbar(error.data?.message || "Failed to create shape", {
          variant: "error",
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
        });
        if (featureGroupRef.current) {
          featureGroupRef.current.clearLayers();
        }
      }
    } else {
      setTempShapeData(shapeData);
      setOpenNameDialog(true);
    }
  };

  // Handle polygon deletion
  const handleDelete = (e) => {
    const { layers } = e;
    layers.eachLayer((layer) => {
      const points = layer.getLatLngs()[0]?.map((point) => ({
        lat: point.lat,
        lng: point.lng,
      }));
      setPolygons((prev) =>
        prev.filter((p) => JSON.stringify(p) !== JSON.stringify(points))
      );
      h;
    });
  };

  // Save polygons and pass them to the parent component
  const handleSave = () => {
    onSavePolygon(polygons);
  };

  // Update the handleNameSubmit function
  const handleNameSubmit = async () => {
    if (tempShapeData && shapeName.trim()) {
      try {
        const payload = {
          name: shapeName.trim(),
          shapeId: tempShapeData.id,
          coordinates: tempShapeData.coordinates.map((coord) => ({
            longitude: coord.lng,
            latitude: coord.lat,
          })),
        };

        const response = await (tempShapeData.areaType === "polyline"
          ? createRoute(payload)
          : createPolygon(payload)
        ).unwrap();

        if (response?.status === 0) {
          const finalShapeData = {
            ...tempShapeData,
            name: shapeName.trim(),
            shapeId: tempShapeData.id,
            // color: tempShapeData.color,
            coordinates: tempShapeData.coordinates.map((coord) => ({
              latitude: coord.lat,
              longitude: coord.lng,
              // ...(coord.radius && { radius: coord.radius }),
            })),
            areaType:
              tempShapeData.areaType === "polyline" ? "route" : "polygon",
            db_id:
              response?.data?.id ||
              polygonList?.find((item) => item?.shapeId === tempShapeData?.id)
                ?.id ||
              "",
          };
          // Update folder structure
          handleNewFolderStructure(finalShapeData);
          setCurrentFolderId(finalShapeData.id);

          // Force re-render of the map
          if (featureGroupRef.current) {
            const map = featureGroupRef.current._map;
            if (map) {
              setTimeout(() => {
                map.invalidateSize();
              }, 100);
            }
          }

          enqueueSnackbar(
            response?.messageDetail
              ? response.messageDetail
              : `${
                  tempShapeData.areaType === "polyline" ? "Route" : "Geofence"
                } created successfully`,
            {
              variant: "success",
              anchorOrigin: {
                vertical: "top",
                horizontal: "right",
              },
            }
          );
        }
      } catch (error) {
        console.error("Error creating shape:", error);
        enqueueSnackbar(error.data?.message || "Failed to create shape", {
          variant: "error",
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
        });
        if (featureGroupRef.current) {
          featureGroupRef.current.clearLayers();
        }
      }

      setOpenNameDialog(false);
      setShapeName("");
      setTempShapeData(null);
    }
  };

  useEffect(() => {
    if (currentFolderId) {
      const foundFolder = findFolderById(folders, currentFolderId);
      if (foundFolder) {
        setCurrentFolder(foundFolder);
      }
    }
  }, [currentFolderId]);

  const handleNameDialogClose = () => {
    // If user cancels, remove the drawn shape
    if (featureGroupRef.current) {
      featureGroupRef.current.clearLayers();
    }
    setOpenNameDialog(false);
    setShapeName("");
    setTempShapeData(null);
  };

  // Recursive function to find a folder by id
  const findFolderById = (folders, id) => {
    for (const folder of folders) {
      if (folder.id === id) {
        return folder;
      }
      if (folder.children) {
        const found = findFolderById(folder.children, id);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  // Function to fetch geojson data based on latitude and longitude
  const fetchGeoJSON = async (query) => {
    // const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&polygon_geojson=1&lat=${lat}&lon=${lon}`;
    // const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&addressdetails=1`;
    const url = `https://nominatim.openstreetmap.org/search?q=${query}&format=json&addressdetails=1&limit=5&polygon_geojson=1`;
    const { data } = await axios.get(url);
    return data;
  };

  useEffect(() => {
    const updateGeoJSON = async () => {
      if (currentLocation) {
        const [lat, lon, query] = currentLocation;
        const geoJSONData = await fetchGeoJSON(query);
        const currentLocationGeoJSON = geoJSONData[0]?.geojson; // Assuming the first result is the desired one

        setGeoLocationJSONData(currentLocationGeoJSON); // Set the GeoJSON data
        // Use currentLocationGeoJSON as needed in your application
        if (mapRef.current) {
          const map = mapRef.current;

          // Remove any existing border layer
          if (map.currentLocationBorder) {
            map.removeLayer(map.currentLocationBorder);
          }

          // Check if the GeoJSON type is a Point
          if (currentLocationGeoJSON.type === "Point") {
            const [lng, lat] = currentLocationGeoJSON.coordinates;
            const pointerIcon = L.divIcon({
              className: "custom-pointer-icon",
              html: `<svg xmlns="http://www.w3.org/2000/svg" xml:space="preserve" width="40" height="40" style="shape-rendering:geometricPrecision;text-rendering:geometricPrecision;image-rendering:optimizeQuality;fill-rule:evenodd;clip-rule:evenodd" viewBox="0 0 6.827 6.827"><defs><style>.fil2{fill:none}</style></defs><g id="Layer_x0020_1"><path style="fill:#00897b;fill-rule:nonzero" d="M3.413 3.764H5.07l.024.084.581 1.976.044.15H3.414z"/><path style="fill:#26a69a;fill-rule:nonzero" d="M1.844 3.764h1.569v2.21H1.107l.626-2.126.024-.084z"/><g id="_490154376"><path id="_490154424" class="fil2" d="M0 0h6.827v6.827H0z"/><path id="_490154760" class="fil2" d="M.853.853h5.12v5.12H.853z"/></g><path d="M4.808 2.357c0 .612-.587 1.418-1.027 2.021-.102.14-.196.27-.272.381l-.096.142-.096-.142c-.075-.112-.168-.24-.27-.381-.438-.607-1.029-1.424-1.029-2.03 0-.411.156-.781.405-1.05.254-.273.606-.44.99-.44.385 0 .736.169.99.443.25.27.405.643.405 1.056z" style="fill:#ffcc80"/><path d="M3.413 2.872a.666.666 0 0 1-.665-.658.666.666 0 0 1 1.33 0 .666.666 0 0 1-.665.658z" style="fill:#ffa726"/></g></svg>`,
              iconSize: [24, 24],
              iconAnchor: [12, 24],
            });
            L.marker([lat, lng], { icon: pointerIcon }).addTo(map);
          } else {
            // Add the GeoJSON layer for the region
            map.currentLocationBorder = L.geoJSON(currentLocationGeoJSON, {
              style: {
                color: "black", // Border color
                weight: 2, // Border width
                fillColor: "blue", // Interior fill color
                fillOpacity: 0.01, // Fill transparency
              },
            }).addTo(map);
          }
        }
      }
    };

    updateGeoJSON();
  }, [currentLocation]);

  // Function to find and update the folder by id
  const updateFolderById = (folders, id, newDbId, newShapeId) => {
    for (const folder of folders) {
      if (folder.id === id) {
        // Update the folder's db_id and shapesId
        folder.db_id = newDbId;
        if (!folder.shapesId.includes(newShapeId)) {
          folder.shapesId.push(newShapeId);
        }
        return true; // Return true if the folder is found and updated
      }
      if (folder.children) {
        const found = updateFolderById(
          folder.children,
          id,
          newDbId,
          newShapeId
        );
        if (found) return true; // Return true if the folder is found in children
      }
    }
    return false; // Return false if the folder is not found
  };

  // Example usage
  const handleUpdateFolder = (folderId, newDbId, newShapeId) => {
    const foldersCopy = JSON.parse(JSON.stringify(folders)); // Deep copy to avoid direct state mutation
    const isUpdated = updateFolderById(
      foldersCopy,
      folderId,
      newDbId,
      newShapeId
    );

    if (isUpdated) {
      setFolders(foldersCopy); // Update the state with the modified folders
      changeGeometryDataJSON(foldersCopy, `updated ${currentFolder?.areaType}`);
    } else {
      console.error(`Folder with id ${folderId} not found.`);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 55px)",
      }}
    >
      <MapHeader
        folderPath={folderPath}
        handleFolderClick={handleFolderClick}
        currentFolder={currentFolder}
        handleHistoryFilterChange={handleHistoryFilterChange}
        setCurrentLocation={setCurrentLocation}
        mapRef={mapRef}
        toggleUserInfo={toggleUserInfo}
        setToggleUserInfo={setToggleUserInfo}
      />

      {/* Map container */}
      {toggleUserInfo && currentFolder?.type === "user" ? (
        <UserShiftDetails
          currentFolder={currentFolder}
          userShiftDetails={userShiftDetails}
        />
      ) : (
        <CustomMapContainer
          currentLocation={currentLocation}
          zoomLevel={zoomLevel}
          isFullScreen={isFullScreen}
          toggleFullScreen={toggleFullScreen}
          drawingColor={drawingColor}
          handleCreate={handleCreate}
          handleDelete={handleDelete}
          featureGroupRef={featureGroupRef}
          mapRef={mapRef}
          bounds={bounds}
          currentFolder={currentFolder}
          polygonList={polygonList}
          getShapesIdsForFolder={getShapesIdsForFolder}
          geoLocationJSONData={geoLocationJSONData}
          showUserLocations={showUserLocations}
          setShowUserLocations={setShowUserLocations}
          activeDrawingTool={activeDrawingTool}
          handleColorPickerOpen={handleColorPickerOpen}
          handleColorPickerClose={handleColorPickerClose}
          colorPickerAnchor={colorPickerAnchor}
          handleColorChange={handleColorChange}
          optionsCheckBox={optionsCheckBox}
          setOptionsCheckBox={setOptionsCheckBox}
        />
      )}
      <ShapeNameDialog
        open={openNameDialog}
        onClose={handleNameDialogClose}
        onSubmit={handleNameSubmit}
        shapeName={shapeName}
        setShapeName={setShapeName}
        shapeType={tempShapeData?.areaType === "polygon" ? "Geofence" : "Route"}
      />
    </Box>
  );
};

export default MapComponent;
